import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './product-offering-price.reducer';

export const ProductOfferingPrice = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const productOfferingPriceList = useAppSelector(state => state.productOfferingPrice.entities);
  const loading = useAppSelector(state => state.productOfferingPrice.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="product-offering-price-heading" data-cy="ProductOfferingPriceHeading">
        <Translate contentKey="crawlerBackendApp.productOfferingPrice.home.title">Product Offering Prices</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.productOfferingPrice.home.refreshListLabel">
              Refresh List
            </Translate>
          </Button>
          <Link
            to="/product-offering-price/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.productOfferingPrice.home.createLabel">
              Create new Product Offering Price
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {productOfferingPriceList && productOfferingPriceList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('effectiveDate')}>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.effectiveDate">
                    Effective Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('effectiveDate')} />
                </th>
                <th className="hand" onClick={sort('expirationDate')}>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.expirationDate">
                    Expiration Date
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('expirationDate')} />
                </th>
                <th className="hand" onClick={sort('description')}>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.description">Description</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('description')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.tariff">Tariff</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.taxTariffTranche">
                    Tax Tariff Tranche
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.gridOperatorCapacity">
                    Grid Operator Capacity
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.productOfferingPrice.productOffering">
                    Product Offering
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productOfferingPriceList.map((productOfferingPrice, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/product-offering-price/${productOfferingPrice.id}`} color="link" size="sm">
                      {productOfferingPrice.id}
                    </Button>
                  </td>
                  <td>
                    {productOfferingPrice.effectiveDate ? (
                      <TextFormat
                        type="date"
                        value={productOfferingPrice.effectiveDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>
                    {productOfferingPrice.expirationDate ? (
                      <TextFormat
                        type="date"
                        value={productOfferingPrice.expirationDate}
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    ) : null}
                  </td>
                  <td>{productOfferingPrice.description}</td>
                  <td>
                    {productOfferingPrice.tariffs
                      ? productOfferingPrice.tariffs.map((val, j) => (
                          <span key={j}>
                            <Link to={`/tariff/${val.id}`}>{val.id}</Link>
                            {j === productOfferingPrice.tariffs.length - 1 ? '' : ', '}
                          </span>
                        ))
                      : null}
                  </td>
                  <td>
                    {productOfferingPrice.taxTariffTranches
                      ? productOfferingPrice.taxTariffTranches.map((val, j) => (
                          <span key={j}>
                            <Link to={`/tax-tariff-tranche/${val.id}`}>{val.id}</Link>
                            {j === productOfferingPrice.taxTariffTranches.length - 1 ? '' : ', '}
                          </span>
                        ))
                      : null}
                  </td>
                  <td>
                    {productOfferingPrice.gridOperatorCapacities
                      ? productOfferingPrice.gridOperatorCapacities.map((val, j) => (
                          <span key={j}>
                            <Link to={`/grid-operator-capacity/${val.id}`}>{val.id}</Link>
                            {j === productOfferingPrice.gridOperatorCapacities.length - 1 ? '' : ', '}
                          </span>
                        ))
                      : null}
                  </td>
                  <td>
                    {productOfferingPrice.productOffering ? (
                      <Link to={`/product-offering/${productOfferingPrice.productOffering.id}`}>
                        {productOfferingPrice.productOffering.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/product-offering-price/${productOfferingPrice.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/product-offering-price/${productOfferingPrice.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/product-offering-price/${productOfferingPrice.id}/delete`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.productOfferingPrice.home.notFound">
                No Product Offering Prices found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ProductOfferingPrice;
